type Loader = () => Promise<unknown> | undefined

/**
 * Клиентские перезагрузки курсорного списка отзывов (сортировка, фильтр, «Показать ещё»,
 * перечитывание после действия): без unhandled rejection — сбой показывается тостом,
 * уже загруженный список остаётся (`useCursorListing` при ошибке коллекцию не трогает),
 * а запомненный загрузчик можно повторить кнопкой «Повторить».
 *
 * @param initialRetry повтор для сбоя первой страницы на SSR (`error` из `useCursorListing`)
 */
export function useListingLoads(initialRetry?: Loader) {
  const toast = useToast()
  /** Что повторить; пусто — последняя загрузка прошла */
  const failedLoad = shallowRef<Loader | undefined>(initialRetry)

  /**
   * Выполнить загрузку. `retryWith` — чем повторять, если повтор отличается от самой загрузки:
   * после сбоя «Показать ещё» `nextPage()` уже ничего не делает (`hasAfter` сброшен),
   * а курсор следующей страницы остался в запросе — повторяется `load(false)`.
   */
  async function run(loader: Loader, retryWith: Loader = loader): Promise<boolean> {
    try {
      await loader()
      failedLoad.value = undefined
      return true
    } catch (error) {
      console.warn('[reviews] не удалось загрузить отзывы:', error)
      failedLoad.value = retryWith
      toast.add({ color: 'error', title: 'Ошибка', description: 'Не удалось загрузить отзывы. Попробуйте ещё раз' })
      return false
    }
  }

  function retry() {
    const loader = failedLoad.value
    return loader ? run(loader) : Promise.resolve(true)
  }

  return {
    /** Последняя загрузка не удалась */
    failed: computed(() => failedLoad.value !== undefined),
    run,
    retry,
  }
}
